import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Button, 
  Box, 
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import { 
  signInWithPopup,
  GoogleAuthProvider,
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { User } from 'firebase/auth';
import { auth } from './config/firebase';
import { wrappedAdminInviteAPI } from './services/admin-api';
import AdminPage from './AdminPage';
import UsersList from './components/Users/UsersList';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import ErrorBoundary from './ErrorBoundary';
import { SubscriptionProvider, Paywall, useOrganizationId } from '@platform/core';
import { BillingPage } from './pages/BillingPage';
import { TeamPage } from './pages/TeamPage';


interface Invite {
  id: string;
  status: string;
  createdAt: any;
  expiresAt: any;
  link?: string;
}

function AppContent() {
  const { language, setLanguage, t } = useLanguage();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState<Invite[]>([]);
  // const [sessions, setSessions] = useState<any[]>([]); // Commented out as not currently used
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
      // loadSessions() removed - AdminPage handles its own session loading
    });
    return () => unsubscribe();
  }, []);

  const handleLogin = async () => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      
      // After successful login, you might want to check/set admin claims
      // This would normally be done server-side
      // loadSessions() removed - AdminPage handles its own session loading
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setInvites([]);
    } catch (error: any) {
      setError(error.message);
    }
  };

  const createInvite = async () => {
    try {
      setError('');
      const result = await wrappedAdminInviteAPI.createInvite(
        'demo-company',
        'demo-assessment',
        120
      );
      
      const data = result as any;
      setSuccess(`Invite created! Link: ${data.link}`);
      loadInvites();
    } catch (error: any) {
      setError(`Failed to create invite: ${error.message}`);
    }
  };

  // Removed loadSessions - AdminPage component handles its own session loading
  // This was causing duplicate API calls

  const loadInvites = async () => {
    try {
      const result = await wrappedAdminInviteAPI.listInvites(
        'demo-company',
        'demo-assessment'
      );
      
      const data = result as any;
      setInvites(data.invites || []);
    } catch (error: any) {
      setError(`Failed to load invites: ${error.message}`);
    }
  };

  const expireInvite = async (inviteId: string) => {
    try {
      await wrappedAdminInviteAPI.expireInvite(
        'demo-company',
        'demo-assessment',
        inviteId
      );
      
      setSuccess('Invite expired successfully');
      loadInvites();
    } catch (error: any) {
      setError(`Failed to expire invite: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <div>
              <Typography variant="h5" style={{fontWeight:700,marginBottom:2}}>
                TurnKeyOS<span style={{color:'#3b82f6'}}>.AI</span>
              </Typography>
              <Typography variant="h4" gutterBottom>
                {language === 'en' ? 'Admin Login' : 'כניסת מנהל'}
              </Typography>
            </div>
            <Tooltip title={language === 'en' ? 'Switch to Hebrew' : 'החלף לאנגלית'}>
              <IconButton onClick={() => setLanguage(language === 'en' ? 'he' : 'en')}>
                <LanguageIcon />
              </IconButton>
            </Tooltip>
          </Box>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Button 
            variant="contained" 
            onClick={handleLogin}
            fullWidth
            color="primary"
          >
            {language === 'en' ? 'Sign in with Google' : 'התחבר עם Google'}
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 2, textAlign: 'center' }}>
            {language === 'en' ? 'Note: Admin access must be granted by system administrator' : 'הערה: גישת מנהל חייבת להינתן על ידי מנהל המערכת'}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">
            {t.dashboard}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2">
              {user.email}
            </Typography>
            <Tooltip title={language === 'en' ? 'Switch to Hebrew' : 'החלף לאנגלית'}>
              <IconButton onClick={() => setLanguage(language === 'en' ? 'he' : 'en')} size="small">
                <LanguageIcon />
              </IconButton>
            </Tooltip>
            <Button variant="outlined" onClick={handleLogout}>
              {language === 'en' ? 'Logout' : 'יציאה'}
            </Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
          <Tab label={t.sessions} />
          <Tab label={language === 'en' ? 'Invites' : 'הזמנות'} />
          <Tab label={t.users} />
          <Tab label={language === 'en' ? 'Team' : 'צוות'} />
          <Tab label={language === 'en' ? 'Billing' : 'חיוב'} />
        </Tabs>

        {tabValue === 0 && (
          <Box>
            <AdminPage />
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {language === 'en' ? 'Invite Management' : 'ניהול הזמנות'}
            </Typography>

            <Box mb={3}>
              <Button 
                variant="contained" 
                onClick={createInvite}
                sx={{ mr: 2 }}
              >
                {language === 'en' ? 'Create New Invite' : 'צור הזמנה חדשה'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={loadInvites}
              >
                {t.refresh}
              </Button>
            </Box>

            <Typography variant="h6" gutterBottom>
              {language === 'en' ? 'Active Invites' : 'הזמנות פעילות'}
            </Typography>
            
            <List>
              {invites.map((invite, index) => (
                <React.Fragment key={invite.id}>
                  <ListItem>
                    <ListItemText
                      primary={`Invite ${invite.id}`}
                      secondary={
                        <>
                          <Typography component="span" variant="body2">
                            {t.status}: {invite.status}
                          </Typography>
                          <br />
                          <Typography component="span" variant="body2">
                            {language === 'en' ? 'Expires' : 'תפוגה'}: {invite.expiresAt?.toDate?.()?.toLocaleString() || 'Unknown'}
                          </Typography>
                          {invite.link && (
                            <>
                              <br />
                              <Typography component="span" variant="body2" color="primary">
                                Link: {invite.link}
                              </Typography>
                            </>
                          )}
                        </>
                      }
                    />
                    {invite.status === 'open' && (
                      <Button 
                        color="error" 
                        onClick={() => expireInvite(invite.id)}
                      >
                        {language === 'en' ? 'Expire' : 'בטל'}
                      </Button>
                    )}
                  </ListItem>
                  {index < invites.length - 1 && <Divider />}
                </React.Fragment>
              ))}
              {invites.length === 0 && (
                <ListItem>
                  <ListItemText primary={language === 'en' ? 'No invites found' : 'לא נמצאו הזמנות'} />
                </ListItem>
              )}
            </List>
          </Box>
        )}

        {tabValue === 2 && (
          <Box>
            <UsersList />
          </Box>
        )}

        {tabValue === 3 && (
          <Box>
            <TeamPage />
          </Box>
        )}

        {tabValue === 4 && (
          <Box>
            <BillingPage />
          </Box>
        )}
      </Paper>
    </Container>
  );
}

function AppWithSubscription() {
  const { organizationId, isAdmin } = useOrganizationId();

  return (
    <SubscriptionProvider organizationId={organizationId}>
      <Paywall isAdmin={isAdmin}>
        <AppContent />
      </Paywall>
    </SubscriptionProvider>
  );
}

function App() {
  return (
    <LanguageProvider>
      <ErrorBoundary>
        <AppWithSubscription />
      </ErrorBoundary>
    </LanguageProvider>
  );
}

export default App;